import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks'
import { useGetFubPonds } from '@forwardflow/hooks/customHooks/stageMapping/fubPonds'
import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'
import Icon from '@forwardflow/ui/Icons'
import { getFilterIcon } from '@forwardflow/utils/filterIcons'
import { Skeleton } from 'antd'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

export const CampaignFilterDetails = () => {
   const { title: leadSource } = useParams()
   const navigate = useNavigate()
   const location = useLocation()
   const { data: activeLeadSourceData, isLoading } = useGetActiveLeadSource(
      leadSource as string
   )

   const hasCampaignFiltersChanges =
      activeLeadSourceData?.draft?.changedSections?.includes('campaign_filters')

   const { data: campaignDetails } = useGetActiveLeadSourceDetails(
      leadSource as string
   )

   const { data: pondList } = useGetFubPonds({
      page: 1,
      limit: 10000,
      search: '',
   })

   const dataSource = hasCampaignFiltersChanges
      ? campaignDetails
      : activeLeadSourceData

   const eligibleCriteria = dataSource?.data?.campaignFilters?.eligibleCriteria
   const sourceCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria) => criteria.field === 'source')
      : null
   const stageCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria) => criteria.field === 'stage')
      : null
   const tagsCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria) => criteria.field === 'tags')
      : null
   const createdCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria) => criteria.field === 'created_at')
      : null
   const ageCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find((criteria) => criteria.field === 'age')
      : null
   const pondsCriteria = Array.isArray(eligibleCriteria)
      ? eligibleCriteria.find(
           (criteria) => criteria.field === 'assigned_pond_id'
        )
      : null
   const sources = Array.isArray(sourceCriteria?.value)
      ? sourceCriteria.value
      : sourceCriteria?.value
      ? [sourceCriteria.value]
      : []
   const stages = Array.isArray(stageCriteria?.value)
      ? stageCriteria.value
      : stageCriteria?.value
      ? [stageCriteria.value]
      : []
   const tags = Array.isArray(tagsCriteria?.value)
      ? tagsCriteria.value
      : tagsCriteria?.value
      ? [tagsCriteria.value]
      : []
   const created = createdCriteria ? createdCriteria.value : null
   const createdOperator = createdCriteria?.operator || ''
   const age = ageCriteria ? ageCriteria.value : null
   const ageOperator = ageCriteria?.operator || ''
   const ponds = pondsCriteria?.value || []
   const { openAdvanceTargetingCampaignModal } =
      useAdvanceTargetingCampaignModalStore()

   const handleEditClick = () => {
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('edit', 'true')
      navigate(`${location.pathname}?${searchParams.toString()}`, {
         replace: true,
      })

      openAdvanceTargetingCampaignModal()
   }
   if (isLoading && sources.length === 0 && eligibleCriteria?.length === 0) {
      return (
         <div className="w-full px-8 my-4">
            <Skeleton active paragraph={{ rows: 1 }} />
         </div>
      )
   }
   if (eligibleCriteria?.length === 0) {
      return false
   }

   return (
      <div className="relative w-full py-3 pl-4 pr-8 text-xs border-b bg-white/5 border-white/10 min-h-20">
         <div
            onClick={handleEditClick}
            className="absolute scale-[1.30] cursor-pointer top-8 right-2"
         >
            <Icon
               name="Edit"
               className="w-5 h-5 transition-colors cursor-pointer text-white/60 hover:text-white"
            />
         </div>
         {/* Sources */}
         {sources.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('source')}</span>

                  <span className="block w-12 mr-2 text-white/60">
                     Sources:
                  </span>
                  <span className="font-medium text-white ">
                     {sources.join(', ')}
                  </span>
               </div>
            </div>
         )}

         {/* Stage */}
         {stages.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('stages')}</span>
                  <span className="block w-12 mr-2 text-white/60 shrink-0">
                     Stage:
                  </span>
                  <span className="font-medium text-white">
                     {stages.join(', ')}
                  </span>
               </div>
            </div>
         )}

         {/* Tags */}
         {tags.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('tags')}</span>

                  <span className="block w-12 mr-2 text-white/60 shrink-0">
                     Tags:
                  </span>
                  <span className="font-medium text-white">
                     {tags.join(', ')}
                  </span>
               </div>
            </div>
         )}

         {/* Created */}
         {created && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('created')}</span>

                  <span className="block w-12 mr-2 text-white/60 shrink-0">
                     Created:
                  </span>
                  <span className="font-medium text-white ">
                     {createdOperator === 'BETWEEN' &&
                     Array.isArray(created) &&
                     created.length === 2
                        ? `Between ${created[0]} and ${created[1]}`
                        : `${
                             createdOperator === '<='
                                ? 'Before'
                                : createdOperator === '>='
                                ? 'After'
                                : createdOperator
                          } ${created}`}
                  </span>
               </div>
            </div>
         )}

         {/* Age */}
         {age && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('age')}</span>

                  <span className="block w-12 mr-2 text-white/60 shrink-0">
                     Age:
                  </span>
                  <span className="font-medium text-white">
                     {`${
                        ageOperator === '<='
                           ? 'Less than'
                           : ageOperator === '>='
                           ? 'Greater than'
                           : ageOperator === '<'
                           ? 'Less than'
                           : ageOperator === '>'
                           ? 'Greater than'
                           : ageOperator
                     } ${age}`}
                  </span>
               </div>
            </div>
         )}

         {/* Ponds */}
         {ponds.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 ">
               <div className="flex items-center">
                  <span className="shrink-0">{getFilterIcon('Ponds')}</span>

                  <span className="block w-12 mr-2 text-white/60 shrink-0">
                     Ponds:
                  </span>
                  <span className="font-medium text-white">
                     {ponds
                        .map((pondId: any) => {
                           const pond = pondList?.find(
                              (p: any) => p.pondId === Number(pondId)
                           )
                           return pond?.pondName || pondId
                        })
                        .join(', ')}
                  </span>
               </div>
            </div>
         )}
      </div>
   )
}
